/**
 * Appointment actions — the legacy TimeSlot context-menu case, one declarative
 * object per verb. Labels/hotkeys mirror the legacy menu (P/Ü/V/N/R/X/B/W/I)
 * so muscle memory transfers.
 *
 * Dialog-opening actions capture their AppointmentDto SNAPSHOT here, at run
 * time, and ship it in the dialog payload — the capture-on-open stability the
 * legacy wrapper needed a dedicated effect for.
 */
import {
  Ban,
  CheckCircle2,
  ClipboardList,
  FileText,
  Info,
  Layers,
  MessageSquareText,
  Pencil,
  PencilLine,
  Replace,
  Megaphone,
} from "lucide-react";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import { AppointmentCtx, ActionServices, CalendarAction, forType } from "./types";

/** Shared preamble for dialog verbs: focus the patient like the legacy menu. */
const selectPatient = (ctx: AppointmentCtx, services: ActionServices) => {
  try {
    services.calendarStore.setSelectedPatientId(ctx.patientId);
  } catch {
    /* selection is a nicety, never a blocker */
  }
};

const lookupOrNotify = (ctx: AppointmentCtx, services: ActionServices) => {
  const appointment = ctx.appointmentId
    ? services.lookupByAppointmentId(ctx.appointmentId)
    : undefined;
  if (!appointment) {
    services.notify("Termin wurde nicht im Cache gefunden.", "error");
  }
  return appointment;
};

export const openPatient: CalendarAction<AppointmentCtx> = {
  id: "openPatient",
  group: "navigate",
  icon: Info,
  label: "Patient",
  hotkey: "p",
  when: forType("appointment"),
  run: (ctx, services) =>
    services.navigateToPatient(ctx.patientId, null, PATIENT_SUBTABS.APPOINTMENTS),
};

export const openAppointmentOverview: CalendarAction<AppointmentCtx> = {
  id: "openAppointmentOverview",
  group: "navigate",
  icon: ClipboardList,
  label: "Terminübersicht",
  hotkey: "ü",
  when: forType("appointment"),
  run: (ctx, services) => {
    selectPatient(ctx, services);
    services.openDialog({
      kind: "appointmentOverview",
      patientId: ctx.patientId,
      rxId: ctx.slot.rx?.id,
    });
  },
};

export const openPrescription: CalendarAction<AppointmentCtx> = {
  id: "openPrescription",
  group: "navigate",
  icon: FileText,
  label: "Rezept",
  hotkey: "r",
  when: forType("appointment"),
  run: (ctx, services) =>
    services.navigateToPatient(
      ctx.patientId,
      ctx.slot.rx?.id ?? null,
      PATIENT_SUBTABS.PRESCRIPTIONS,
    ),
};

export const openNotes: CalendarAction<AppointmentCtx> = {
  id: "openNotes",
  group: "navigate",
  icon: MessageSquareText,
  label: "Terminhinweise",
  hotkey: "n",
  when: forType("appointment", (ctx) => !ctx.isContinuous && !!ctx.appointmentId),
  run: (ctx, services) => {
    selectPatient(ctx, services);
    services.calendarStore.openNotesDialog(ctx.appointmentId!);
  },
};

/**
 * MD-302: pick a non-default therapy method for the clicked slot. The dialog
 * itself explains when the term has no alternatives; gated to concrete,
 * non-cancelled treatment slots (Dauertermin slots re-resolve per occurrence
 * and are out of scope for the first increment).
 */
export const chooseTherapyMethod: CalendarAction<AppointmentCtx> = {
  id: "chooseTherapyMethod",
  group: "modify",
  icon: Layers,
  label: "Alternative Methoden",
  when: forType(
    "appointment",
    (ctx) => !ctx.isContinuous && !ctx.isCancelled && !!ctx.slot.id,
  ),
  run: (ctx, services) => {
    services.openDialog({ kind: "therapyMethod", timeSlotId: String(ctx.slot.id) });
  },
};

/**
 * Umziehen: lift onto the pinboard (client-side; nothing persists until the
 * pin is dropped). Gated to non-continuous — the controller verb resolves the
 * appointment from the regular cache, so in Dauertermin it would silently
 * no-op (legacy showed the item there and did exactly that; we'd rather not
 * offer it).
 */
export const moveAppointment: CalendarAction<AppointmentCtx> = {
  id: "moveAppointment",
  group: "modify",
  icon: Replace,
  label: (ctx) =>
    ctx.raw.extendedProps?.multipleTimeSlots ? "Verschieben (ganzer Termin)" : "Verschieben",
  hotkey: "v",
  when: forType("appointment", (ctx) => !ctx.isContinuous && !ctx.isCancelled),
  run: (ctx, services) => void services.controller()?.onMoveEventDate(ctx.raw),
};

/**
 * "Nur diese": pin a SINGLE slot of a multi-slot appointment. Shown as its
 * own item instead of the legacy nested Alle/Nur-diese dialog — one less
 * modal, and both choices are visible up front.
 */
export const moveSingleSlot: CalendarAction<AppointmentCtx> = {
  id: "moveSingleSlot",
  group: "modify",
  icon: Replace,
  label: "Verschieben (nur diese Behandlung)",
  when: forType(
    "appointment",
    (ctx) =>
      !ctx.isContinuous && !ctx.isCancelled && !!ctx.raw.extendedProps?.multipleTimeSlots,
  ),
  run: (ctx, services) =>
    void services.controller()?.onMoveEventDate({
      ...ctx.raw,
      extendedProps: { ...ctx.raw.extendedProps, moveSingleSlot: true },
    }),
};

export const editAppointment: CalendarAction<AppointmentCtx> = {
  id: "editAppointment",
  group: "modify",
  icon: Pencil,
  label: "Bearbeiten",
  hotkey: "b",
  when: forType("appointment", (ctx) => !ctx.isCancelled),
  run: (ctx, services) => {
    const appointment = lookupOrNotify(ctx, services);
    if (!appointment) return;
    selectPatient(ctx, services);
    services.openDialog({
      kind: "editAppointment",
      appointment,
      patientId: ctx.patientId,
      isContinuous: ctx.isContinuous,
    });
  },
};

export const toggleAttended: CalendarAction<AppointmentCtx> = {
  id: "toggleAttended",
  group: "modify",
  icon: CheckCircle2,
  label: "Anwesenheit ändern",
  hotkey: "w",
  when: forType("appointment", (ctx) => !ctx.isContinuous && !ctx.isCancelled),
  run: (ctx, services) => {
    const appointment = lookupOrNotify(ctx, services);
    if (!appointment) return;
    selectPatient(ctx, services);
    services.openDialog({ kind: "attendance", appointment, patientId: ctx.patientId });
  },
};

export const setInformed: CalendarAction<AppointmentCtx> = {
  id: "setInformed",
  group: "modify",
  icon: Megaphone,
  label: "Patient informieren",
  hotkey: "i",
  when: forType("appointment", (ctx) => !ctx.isContinuous && !ctx.isCancelled),
  run: (ctx, services) => {
    selectPatient(ctx, services);
    services.openDialog({ kind: "setInformed", slot: ctx.slot });
  },
};

export const cancelAppointment: CalendarAction<AppointmentCtx> = {
  id: "cancelAppointment",
  group: "danger",
  icon: Ban,
  label: "Stornieren",
  hotkey: "x",
  when: forType("appointment", (ctx) => !ctx.isCancelled),
  run: (ctx, services) => {
    selectPatient(ctx, services);
    if (ctx.isContinuous) {
      if (!ctx.appointmentId) return;
      services.openDialog({ kind: "cancelContinuous", appointmentId: ctx.appointmentId });
      return;
    }
    const appointment = lookupOrNotify(ctx, services);
    if (!appointment) return;
    services.openDialog({
      kind: "cancelAppointment",
      appointment,
      patientId: ctx.patientId,
      initialSlotIds: ctx.slot.id ? [ctx.slot.id] : undefined,
    });
  },
};

export const editCancellationReason: CalendarAction<AppointmentCtx> = {
  id: "editCancellationReason",
  group: "modify",
  icon: PencilLine,
  label: "Abbruchgrund bearbeiten",
  when: forType("appointment", (ctx) => ctx.isCancelled),
  run: (ctx, services) => {
    const appointment = lookupOrNotify(ctx, services);
    if (!appointment) return;
    selectPatient(ctx, services);
    services.openDialog({ kind: "editCancellation", appointment, patientId: ctx.patientId });
  },
};
